/**
 * Connection Creation Service
 *
 * Handles automatic connection creation for connectors that don't require user input.
 * Based on LogicAppsUX autoCreateConnectionIfPossible pattern.
 */

import type { ConnectionReference } from '@/types/connectionReference';
import type { ConnectionParameterSet } from '@/types/connectionParameters';
import { AppResourceContext, createConnection, toConnectionReference } from './connectionApi';
import { generateConnectionName } from './manifestConnectionService';

/**
 * Connection parameter types (lowercase for comparison with API values)
 */
const ConnectionParameterTypes = {
  string: 'string',
  secureString: 'securestring',
  int: 'int',
  bool: 'bool',
  array: 'array',
  object: 'object',
  secureObject: 'secureobject',
  oauthSetting: 'oauthsetting',
  managedIdentity: 'managedidentity',
  gatewaySetting: 'gatewaysetting',
  connection: 'connection',
  clientCertificate: 'clientcertificate',
} as const;
type ConnectionParameterTypes = (typeof ConnectionParameterTypes)[keyof typeof ConnectionParameterTypes];

/**
 * Connection type from Azure
 */
interface Connection {
  id: string;
  name: string;
  properties: {
    connectionRuntimeUrl?: string;
    [key: string]: unknown;
  };
}

/**
 * Connector type from Azure
 */
interface Connector {
  id: string;
  properties?: {
    displayName?: string;
    connectionParameters?: Record<string, ConnectionParameter>;
    connectionParameterSets?: {
      values?: ConnectionParameterSet[];
    };
    connectionAlternativeParameters?: Record<string, ConnectionParameter>;
    metadata?: {
      termsOfServiceUrl?: string;
      termsOfUseUrl?: string;
    };
    isDynamicConnectionAllowed?: boolean;
  };
}

/**
 * Connection parameter definition
 */
interface ConnectionParameter {
  type?: string;
  uiDefinition?: {
    constraints?: {
      hidden?: string;
      hideInUI?: string;
    };
  };
  oAuthSettings?: {
    identityProvider?: string;
    customParameters?: Record<string, unknown>;
    properties?: {
      AzureActiveDirectoryResourceId?: string;
    };
  };
  managedIdentitySettings?: {
    resourceUri?: string;
    additionalResourceUris?: string[];
  };
}

/**
 * Check if a parameter is hidden based on its constraints
 */
function isHiddenConnectionParameter(
  parameters: Record<string, ConnectionParameter>,
  key: string
): boolean {
  const param = parameters[key];
  const constraints = param?.uiDefinition?.constraints;
  return constraints?.hidden === 'true' || constraints?.hideInUI === 'true';
}

/**
 * Check if connector has multi-auth (multiple parameter sets)
 */
export function connectorHasMultiAuth(connector: Connector): boolean {
  return connector?.properties?.connectionParameterSets !== undefined;
}

/**
 * Check if connector has Terms of Service that requires acceptance
 */
export function hasTermsOfUse(connector: Connector): boolean {
  const metadata = connector?.properties?.metadata;
  return !!(metadata?.termsOfServiceUrl || metadata?.termsOfUseUrl);
}

/**
 * Get Terms of Service URL from connector
 */
export function getTermsOfServiceUrl(connector: Connector): string | undefined {
  const metadata = connector?.properties?.metadata;
  return metadata?.termsOfServiceUrl || metadata?.termsOfUseUrl;
}

/**
 * Check if connector needs OAuth authentication
 */
export function needsOAuth(connectionParameters: Record<string, ConnectionParameter>): boolean {
  return Object.entries(connectionParameters || {})
    .filter(([key]) => !isHiddenConnectionParameter(connectionParameters, key))
    .some(([, param]) => param.type?.toLowerCase() === ConnectionParameterTypes.oauthSetting);
}

/**
 * Check if connector has only OAuth parameters (no other visible params)
 */
export function hasOnlyOAuthParameters(connector: Connector): boolean {
  const connectionParameters = connector?.properties?.connectionParameters;
  const connectionParameterSets = connector?.properties?.connectionParameterSets;
  const connectionAlternativeParameters = connector?.properties?.connectionAlternativeParameters;

  // Multi-auth or alternative params means not simple OAuth
  if (connectionParameterSets || connectionAlternativeParameters) {
    return false;
  }

  if (!connectionParameters) {
    return false;
  }

  const visibleParams = Object.entries(connectionParameters)
    .filter(([key]) => !isHiddenConnectionParameter(connectionParameters, key));

  if (visibleParams.length === 0) {
    return false;
  }

  // All visible params must be OAuth
  return visibleParams.every(([, param]) =>
    param.type?.toLowerCase() === ConnectionParameterTypes.oauthSetting
  );
}

/**
 * Check if connector needs a simple connection (no visible parameters)
 */
export function needsSimpleConnection(connector: Connector): boolean {
  if (!connector || connectorHasMultiAuth(connector)) {
    return false;
  }

  const connectionParameters = connector?.properties?.connectionParameters;
  if (!connectionParameters) {
    return true;
  }

  const visibleParams = Object.entries(connectionParameters)
    .filter(([key]) => !isHiddenConnectionParameter(connectionParameters, key));

  return visibleParams.length === 0;
}

/**
 * Check if connector needs configuration (has visible config parameters)
 */
export function needsConfigConnection(connector: Connector): boolean {
  const connectionParameters = connector?.properties?.connectionParameters;
  if (!connectionParameters) {
    return false;
  }

  const configTypes: string[] = [
    ConnectionParameterTypes.string,
    ConnectionParameterTypes.secureString,
    ConnectionParameterTypes.int,
    ConnectionParameterTypes.bool,
    ConnectionParameterTypes.array,
    ConnectionParameterTypes.object,
    ConnectionParameterTypes.secureObject,
    ConnectionParameterTypes.gatewaySetting,
  ];

  return Object.entries(connectionParameters)
    .filter(([key]) => !isHiddenConnectionParameter(connectionParameters, key))
    .some(([, param]) => configTypes.includes(param.type?.toLowerCase() ?? ''));
}

/**
 * Check if connector needs any type of connection
 */
export function needsConnection(connector: Connector | undefined): boolean {
  if (!connector) {
    return false;
  }

  const connectionParameters = connector?.properties?.connectionParameters;

  // Has OAuth parameters
  if (needsOAuth(connectionParameters || {})) {
    return true;
  }

  // Has prerequisite connection parameters
  const hasPrerequisite = Object.values(connectionParameters || {})
    .some(param => param.type?.toLowerCase() === ConnectionParameterTypes.connection);
  if (hasPrerequisite) {
    return true;
  }

  // Simple or config connection
  return needsSimpleConnection(connector) || needsConfigConnection(connector);
}

/**
 * Check if connector is an Agent connector
 */
export function isAgentConnector(connectorId: string): boolean {
  const agentPatterns = [
    '/agentConnections/',
    'Agent',
    'AgentLoop',
    '/providers/Microsoft.Web/connections/agent',
    'connectionProviders/agent',
  ];
  return agentPatterns.some(pattern =>
    connectorId.toLowerCase().includes(pattern.toLowerCase())
  );
}

/**
 * Check if a connector ID is a local connector (not an Azure ARM resource).
 * Local connectors (Agent, ServiceProvider, Function, etc.) should NOT be
 * created via the Azure Connections API. They are stored locally in connections.json.
 *
 * ARM resource IDs start with '/subscriptions/' or have the pattern:
 * /subscriptions/{subId}/providers/Microsoft.Web/locations/{location}/managedApis/{connector}
 */
export function isLocalConnector(connectorId: string): boolean {
  // ARM resource IDs start with /subscriptions/ or are absolute paths with /providers/
  const isArmResourceId =
    connectorId.startsWith('/subscriptions/') ||
    (connectorId.startsWith('/providers/') && connectorId.includes('Microsoft.'));

  // If it's an ARM resource ID, it's NOT a local connector
  if (isArmResourceId) {
    return false;
  }

  // Known local connector patterns
  const localPatterns = [
    '/agentConnections/',
    '/serviceProviders/',
    '/connectionProviders/',
    'connectionProviders/',
    'serviceProviders/',
  ];

  return localPatterns.some(pattern =>
    connectorId.toLowerCase().includes(pattern.toLowerCase())
  ) || isAgentConnector(connectorId);
}

/**
 * Result of auto-create attempt
 */
export interface AutoCreateResult {
  success: boolean;
  connection?: Connection;
  reference?: ConnectionReference;
  referenceKey?: string;
  requiresManualCreation: boolean;
  requiresOAuth: boolean;
  requiresTermsAcceptance: boolean;
  termsOfServiceUrl?: string;
  reason?: string;
}

/**
 * Parameters for auto-create connection
 */
export interface AutoCreateParams {
  connector: Connector;
  existingReferenceKeys: string[];
  skipOAuth?: boolean;
  context: AppResourceContext;
}

/**
 * Attempt to automatically create a connection if possible.
 *
 * Returns success if connection was created without user input.
 * Returns requiresManualCreation if user needs to fill in parameters.
 * Returns requiresOAuth if OAuth flow needs to be initiated.
 * Returns requiresTermsAcceptance if ToS needs to be accepted first.
 *
 * Integration point: This function should be called during node creation
 * (WorkflowDesigner.handleOperationSelect/handleToolboxDrop) when:
 * 1. Operation is a connector operation (isConnector: true)
 * 2. No existing connection matches (findMatchingConnectionReference returns undefined)
 * 3. Connector metadata is available (requires fetching connector by connectorId)
 *
 * Current limitation: Operation selection doesn't fetch full connector metadata,
 * only connectorId is available. Full integration requires adding connector
 * metadata fetching to the operation selection flow.
 */
export async function autoCreateConnectionIfPossible(
  params: AutoCreateParams
): Promise<AutoCreateResult> {
  const { connector, existingReferenceKeys, skipOAuth, context } = params;

  // Agent connectors always require manual creation (need to select model type, resources, etc.)
  if (isAgentConnector(connector.id)) {
    return {
      success: false,
      requiresManualCreation: true,
      requiresOAuth: false,
      requiresTermsAcceptance: false,
      reason: 'Agent connectors require manual configuration',
    };
  }

  // Multi-auth connectors require user to select auth type
  if (connectorHasMultiAuth(connector)) {
    return {
      success: false,
      requiresManualCreation: true,
      requiresOAuth: false,
      requiresTermsAcceptance: false,
      reason: 'Connector has multiple authentication options',
    };
  }

  // Check for Terms of Service requirement
  if (hasTermsOfUse(connector)) {
    const tosUrl = getTermsOfServiceUrl(connector);
    const result: AutoCreateResult = {
      success: false,
      requiresManualCreation: false,
      requiresOAuth: false,
      requiresTermsAcceptance: true,
      reason: 'Terms of Service acceptance required',
    };
    if (tosUrl) {
      result.termsOfServiceUrl = tosUrl;
    }
    return result;
  }

  // Simple connection - no visible parameters
  if (needsSimpleConnection(connector)) {
    try {
      const connectionName = generateConnectionName(
        connector.properties?.displayName ?? connector.id,
        existingReferenceKeys
      );

      const connectionResource = await createConnection(context, {
        connectionName,
        connectorId: connector.id,
        parameterValues: {},
      });

      const reference = toConnectionReference(connectionResource);
      const referenceKey = connectionName.toLowerCase().replace(/\s+/g, '_');

      return {
        success: true,
        connection: connectionResource as unknown as Connection,
        reference,
        referenceKey,
        requiresManualCreation: false,
        requiresOAuth: false,
        requiresTermsAcceptance: false,
      };
    } catch (error) {
      return {
        success: false,
        requiresManualCreation: true,
        requiresOAuth: false,
        requiresTermsAcceptance: false,
        reason: error instanceof Error ? error.message : 'Failed to create simple connection',
      };
    }
  }

  // OAuth-only connection
  if (!skipOAuth && hasOnlyOAuthParameters(connector)) {
    return {
      success: false,
      requiresManualCreation: false,
      requiresOAuth: true,
      requiresTermsAcceptance: false,
      reason: 'OAuth authentication required',
    };
  }

  // Needs configuration
  if (needsConfigConnection(connector)) {
    return {
      success: false,
      requiresManualCreation: true,
      requiresOAuth: needsOAuth(connector.properties?.connectionParameters || {}),
      requiresTermsAcceptance: false,
      reason: 'Configuration parameters required',
    };
  }

  // Default: requires manual creation
  return {
    success: false,
    requiresManualCreation: true,
    requiresOAuth: false,
    requiresTermsAcceptance: false,
    reason: 'Unknown connector type',
  };
}

/**
 * Check if Service Principal authentication is supported
 */
export function supportsServicePrincipal(connector: Connector): boolean {
  const connectionParameters = connector?.properties?.connectionParameters;
  if (!connectionParameters) {
    return false;
  }

  const requiredKeys = [
    'token:clientId',
    'token:clientSecret',
    'token:tenantId',
  ];

  // Check if all required service principal params exist
  const paramKeys = Object.keys(connectionParameters).map(k => k.toLowerCase());
  return requiredKeys.every(key =>
    paramKeys.some(pk => pk.includes(key.replace(':', '').toLowerCase()))
  );
}

/**
 * Check if Client Certificate authentication is supported
 */
export function supportsClientCertificate(connector: Connector): boolean {
  const parameterSets = connector?.properties?.connectionParameterSets?.values;
  if (!parameterSets) {
    return false;
  }

  return parameterSets.some(set =>
    Object.values(set.parameters || {}).some(
      param => param.type?.toLowerCase() === ConnectionParameterTypes.clientCertificate
    )
  );
}

/**
 * Check if Managed Identity authentication is supported (legacy)
 */
export function supportsLegacyManagedIdentity(connector: Connector): boolean {
  const alternativeParams = connector?.properties?.connectionAlternativeParameters;
  if (!alternativeParams) {
    return false;
  }

  return Object.values(alternativeParams).some(
    param => param.type?.toLowerCase() === ConnectionParameterTypes.managedIdentity
  );
}

/**
 * Check if connector uses AAD authentication
 */
export function isUsingAadAuthentication(connector: Connector): boolean {
  const connectionParameters = connector?.properties?.connectionParameters;
  if (!connectionParameters) {
    return false;
  }

  // Check for AAD-specific OAuth settings
  const tokenParam = connectionParameters['token'];
  const oAuthSettings = tokenParam?.oAuthSettings;

  if (!oAuthSettings) {
    return false;
  }

  const identityProvider = oAuthSettings.identityProvider?.toLowerCase();
  return identityProvider === 'aad' || identityProvider === 'azureactivedirectory';
}

/**
 * Check if tenant selection should be shown
 */
export function shouldShowTenantSelection(connector: Connector): boolean {
  if (!isUsingAadAuthentication(connector)) {
    return false;
  }

  const connectionParameters = connector?.properties?.connectionParameters;
  const tokenParam = connectionParameters?.['token'];
  const customParams = tokenParam?.oAuthSettings?.customParameters;

  if (!customParams) {
    return false;
  }

  // Check if tenantId is in custom parameters
  const hasTenantIdCustomParam = Object.keys(customParams)
    .some(key => key.toLowerCase() === 'tenantid');

  // Check if token:TenantId parameter exists
  const hasTenantIdParam = Object.keys(connectionParameters || {})
    .some(key => key.toLowerCase() === 'token:tenantid');

  return hasTenantIdCustomParam && hasTenantIdParam;
}

/**
 * Check if dynamic connections (oBO) are allowed for this connector
 */
export function isDynamicConnectionAllowed(connector: Connector): boolean {
  return connector?.properties?.isDynamicConnectionAllowed === true;
}

/**
 * Get available authentication methods for a connector
 */
export interface AuthMethod {
  id: string;
  name: string;
  description?: string;
  type: 'oauth' | 'servicePrincipal' | 'managedIdentity' | 'apiKey' | 'clientCertificate' | 'basic';
  isLegacy?: boolean;
}

export function getAvailableAuthMethods(connector: Connector): AuthMethod[] {
  const methods: AuthMethod[] = [];

  // Multi-auth from parameter sets
  const parameterSets = connector?.properties?.connectionParameterSets?.values;
  if (parameterSets) {
    parameterSets.forEach(set => {
      const hasOAuth = Object.values(set.parameters || {}).some(
        p => p.type?.toLowerCase() === ConnectionParameterTypes.oauthSetting
      );
      const hasManagedIdentity = Object.values(set.parameters || {}).some(
        p => p.type?.toLowerCase() === ConnectionParameterTypes.managedIdentity
      );
      const hasClientCert = Object.values(set.parameters || {}).some(
        p => p.type?.toLowerCase() === ConnectionParameterTypes.clientCertificate
      );

      const method: AuthMethod = {
        id: set.name,
        name: set.uiDefinition?.displayName ?? set.name,
        type: hasOAuth ? 'oauth' :
              hasManagedIdentity ? 'managedIdentity' :
              hasClientCert ? 'clientCertificate' : 'apiKey',
      };
      if (set.uiDefinition?.description) {
        method.description = set.uiDefinition.description;
      }
      methods.push(method);
    });

    return methods;
  }

  // Legacy single-auth
  const connectionParameters = connector?.properties?.connectionParameters;
  if (connectionParameters) {
    // OAuth
    if (needsOAuth(connectionParameters)) {
      methods.push({
        id: 'legacyoauth',
        name: 'OAuth',
        description: 'Sign in with your account',
        type: 'oauth',
        isLegacy: true,
      });
    }

    // Service Principal
    if (supportsServicePrincipal(connector)) {
      methods.push({
        id: 'legacyserviceprincipal',
        name: 'Service Principal',
        description: 'Authenticate with client ID and secret',
        type: 'servicePrincipal',
        isLegacy: true,
      });
    }
  }

  // Legacy Managed Identity
  if (supportsLegacyManagedIdentity(connector)) {
    methods.push({
      id: 'legacymanagedidentity',
      name: 'Managed Identity',
      description: 'Use Azure Managed Identity',
      type: 'managedIdentity',
      isLegacy: true,
    });
  }

  return methods;
}
